import MainEngineFuelTypeChart from '../../../../components/vessel/cohort-benchmarking/emissions/MainEngineFuelTypeChart';
import EmissionByEcaChart from '../../../../components/vessel/cohort-benchmarking/emissions/EmissionByEcaChart';
import CohortDefinition from '../../../../../common/cohort/components/characteristic/CohortDefinition';
import { ICohortVessel } from '../../../../../common/cohort/cohortTypes';
import { useAppDispatch, useAppSelector } from '../../../../../../core/rootHooks';
import { AppDispatch } from '../../../../../../core/store';
import { useEffect, useRef, useState } from 'react';
import {
	greenActions,
	selectCohortEmissionsOfEcaSeaAreas,
	selectCohortVesselKeyMetricsDateBased,
	selectCohortVesselsEtsAndFuelEuEmissions,
	selectCohortVesselsMainEngineFuelConsumes,
	selectEmissionsOfEcaSeaAreas,
	selectVesselKeyMetricsDateBased,
	selectVesselsEtsAndFuelEuEmissions,
	selectVesselsMainEngineFuelConsumes,
} from '../../../../greenSlice';
import VesselEtsFuelEuEmissionsChart from '../../../../components/vessel/cohort-benchmarking/emissions/VesselEtsFuelEuEmissionsChart';
import LoadingSpinner from '../../../../../../shared/components/loading-spinner/LoadingSpinner';
import ExpandableContainer from '../../../../../../shared/components/expandable-container/ExpandableContainer';
import MetricsCard from '../../../../../../shared/components/metricsCard/MetricsCard';
import { MultiChartDownloadButton } from '../../../../../../shared/components/charts/MultiDataDownloadButton';
import { Dataset } from '../../../../../../utils/downloadHelpers';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../../../date-range/slices/dateRangeSlice';

export interface ICohortEmissionsProps {
	vessel: ICohortVessel;
	cohortVessels: ICohortVessel[] | [];
	minSize: number;
	maxSize: number;
	minAge: number;
	maxAge: number;
}

const CohortEmissions: React.FC<ICohortEmissionsProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const vesselKeyMetricsDateBased = useAppSelector(selectVesselKeyMetricsDateBased);
	const cohortVesselKeyMetricsDateBased = useAppSelector(selectCohortVesselKeyMetricsDateBased);
	const emissionsOfEcaSeaAreas = useAppSelector(selectEmissionsOfEcaSeaAreas);
	const cohortEmissionsOfEcaSeaAreas = useAppSelector(selectCohortEmissionsOfEcaSeaAreas);
	const vesselsEtsAndFuelEuEmissions = useAppSelector(selectVesselsEtsAndFuelEuEmissions);
	const cohortVesselsEtsAndFuelEuEmissions = useAppSelector(selectCohortVesselsEtsAndFuelEuEmissions);
	const vesselsMainEngineFuelConsumes = useAppSelector(selectVesselsMainEngineFuelConsumes);
	const cohortVesselsMainEngineFuelConsumes = useAppSelector(selectCohortVesselsMainEngineFuelConsumes);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);
	
	const cohortVesselInfo = props.cohortVessels.length === 1 ? props.cohortVessels[0] : props.vessel;

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;

			if (!startYearMonth || !endYearMonth) return;

			dispatch(
				greenActions.getEmissionsOfEcaSeaAreasRequestAction({
					imoNumber: props.vessel.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
			dispatch(
				greenActions.getEmissionsOfOperationalPhasesRequestAction({
					imoNumber: props.vessel.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
			dispatch(
				greenActions.getVesselKeyMetricsDateBasedRequestAction({
					imoNumbers: [props.vessel.imoNumber],
					startYearMonth,
					endYearMonth,
				}),
			);
			dispatch(
				greenActions.getVesselsEtsAndFuelEuEmissionsRequestAction({
					imoNumbers: [props.vessel.imoNumber],
					startYearMonth,
					endYearMonth,
				}),
			);
			dispatch(
				greenActions.getVesselsMainEngineFuelConsumesRequestAction({
					imoNumbers: [props.vessel.imoNumber],
					startYearMonth,
					endYearMonth,
				}),
			);

			if (props.cohortVessels.length != 0) {
				const imoList = props.cohortVessels.map((v) => v.imoNumber);
				dispatch(
					greenActions.getCohortEmissionsOfEcaSeaAreasRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
				dispatch(
					greenActions.getCohortEmissionsOfOperationalPhasesRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
				dispatch(
					greenActions.getCohortVesselKeyMetricsDateBasedRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
				dispatch(
					greenActions.getCohortVesselsEtsAndFuelEuEmissionsRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
				dispatch(
					greenActions.getCohortVesselsMainEngineFuelConsumesRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
			}
		}
	}, [dispatch, props.vessel.imoNumber, props.cohortVessels, analysisDateRange]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	const chartDatasets: Dataset<any>[] = [
		{
			name: 'Main Engine CO2 Emissions by Fuel Type',
			description: 'Tank-to-Wake emissions from main engine by fuel type',
			data: vesselsMainEngineFuelConsumes.vesselsMainEngineFuelConsumesData,
		},
		{
			name: 'CO2 Emission Qualifying for ETS',
			description: 'Tank-to-Wake emissions qualifying for European Trading System',
			data: vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData?.map((item) => ({
				qualifying: item.etsTrueSum || 0,
				nonQualifying: item.etsFalseSum || 0,
			})),
		},
		{
			name: 'CO2 Emission Qualifying for FuelEU',
			description: 'Well-to-Wake emissions qualifying for FuelEU Maritime',
			data: vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData?.map((item) => ({
				qualifying: item.fuelEuTrueSum || 0,
				nonQualifying: item.fuelEuFalseSum || 0,
			})),
		},
		{
			name: 'CO2 Emissions by ECA',
			description: 'Tank-to-Wake emissions by Emission Control Area',
			data: emissionsOfEcaSeaAreas.emissionsOfEcaSeaAreasData,
		},
	];

	const cohortChartDatasets: Dataset<any>[] = [
		{
			name: 'Main Engine CO2 Emissions by Fuel Type',
			description: 'Tank-to-Wake emissions from main engine by fuel type',
			data: cohortVesselsMainEngineFuelConsumes.cohortVesselsMainEngineFuelConsumesData,
		},
		{
			name: 'CO2 Emission Qualifying for ETS and FuelEU',
			description: 'Tank-to-Wake emissions qualifying for European Trading System',
			data: cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData?.map((item) => ({
				qualifying: item.etsTrueSum || 0,
				nonQualifying: item.etsFalseSum || 0,
			})),
		},
		{
			name: 'CO2 Emission Qualifying for FuelEU',
			description: 'Well-to-Wake emissions qualifying for FuelEU Maritime',
			data: cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData?.map((item) => ({
				qualifying: item.fuelEuTrueSum || 0,
				nonQualifying: item.fuelEuFalseSum || 0,
			})),
		},
		{
			name: 'CO2 Emissions by ECA',
			description: 'Tank-to-Wake emissions by Emission Control Area',
			data: cohortEmissionsOfEcaSeaAreas.cohortEmissionsOfEcaSeaAreasData,
		},
	];

	return (
		<div ref={ref} className="flex flex-col xl:flex-row gap-4 w-full h-full">
			<div className="flex flex-col w-full gap-2">
				<div className="flex-none">
					{props.cohortVessels && props.cohortVessels.length > 0 && (
						<CohortDefinition
							key="co2EmissionsStats"
							title={props.vessel.name}
							isCohort={false}
							age={props.vessel.age}
							size={props.vessel.size}
							sizeUnit={props.vessel.sizeUnit}
						/>
					)}
				</div>
				<div className="flex flex-row h-full w-full gap-2 border border-msi-darkgrey-color-300 rounded">
					<ExpandableContainer title="CO2 Emissions" titleClassName="font-bold pb-2" panelOffsets={panelOffset}>
						<MultiChartDownloadButton
							datasets={chartDatasets}
							filename="co2_emissions_complete_analysis"
							buttonStyle="primary"
							buttonText="Download All Emissions Data"
						/>
						<div className="flex flex-row h-full w-full gap-2">
							<div className="flex flex-col flex-none gap-1">
								{vesselKeyMetricsDateBased.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
								{!vesselKeyMetricsDateBased.loading &&
									vesselKeyMetricsDateBased &&
									vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData && (
										<MetricsCard
											className="p-2 space-y-6"
											metrics={[
												{
													label: 'Main Engine (Tank-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].mainEngineEmissions,
												},
												{
													label: 'Pilot (Tank-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].mainEnginePilotEmissions,
												},
												{
													label: 'Auxiliary Engine (Tank-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].auxiliaryEngineEmissions,
												},
												{
													label: 'Boiler (Tank-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].boilerEmissions,
												},
												{
													label: 'Total (Tank-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].co2Emissions,
												},
												{
													label: 'Total (Well-to-Wake)',
													value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].co2EmissionsWtW,
												},
											]}
										/>
									)}
							</div>

							<div className="flex flex-col w-full h-full gap-2">
								<div className="flex flex-row h-full">
									{vesselsMainEngineFuelConsumes.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!vesselsMainEngineFuelConsumes.loading &&
										vesselsMainEngineFuelConsumes &&
										vesselsMainEngineFuelConsumes.vesselsMainEngineFuelConsumesData && (
											<MainEngineFuelTypeChart
												key="vessels-mainEngineFuelTypeChart"
												data={vesselsMainEngineFuelConsumes.vesselsMainEngineFuelConsumesData}
											/>
										)}
								</div>
								<div className="flex flex-row h-full">
									{vesselsEtsAndFuelEuEmissions.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!vesselsEtsAndFuelEuEmissions.loading &&
										vesselsEtsAndFuelEuEmissions &&
										vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData &&
										vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData.length > 0 && (
											<VesselEtsFuelEuEmissionsChart
												key="vessels-etsEmissionsChart"
												trueSum={vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData[0].etsTrueSum}
												falseSum={vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData[0].etsFalseSum}
												title="CO2 Emission Qualifying for ETS (Tank-to-Wake)"
											/>
										)}
								</div>
								<div className="flex flex-row h-full">
									{vesselsEtsAndFuelEuEmissions.loading && <LoadingSpinner size="md" spinnerClassName="p-1" />}
									{!vesselsEtsAndFuelEuEmissions.loading &&
										vesselsEtsAndFuelEuEmissions &&
										vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData &&
										vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData.length > 0 && (
											<VesselEtsFuelEuEmissionsChart
												key="vessels-fuelEuEmissionsChart"
												trueSum={vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData[0].fuelEuTrueSum}
												falseSum={vesselsEtsAndFuelEuEmissions.vesselsEtsAndFuelEuEmissionsData[0].fuelEuFalseSum}
												title="CO2 Emission Qualifying for FuelEU (Well-to-Wake)"
											/>
										)}
								</div>
								<div className="flex flex-row h-full">
									{emissionsOfEcaSeaAreas.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!emissionsOfEcaSeaAreas.loading &&
										emissionsOfEcaSeaAreas &&
										emissionsOfEcaSeaAreas.emissionsOfEcaSeaAreasData && (
											<EmissionByEcaChart key="emissionsOfEcaSeaAreas" data={emissionsOfEcaSeaAreas.emissionsOfEcaSeaAreasData} />
										)}
								</div>
							</div>
						</div>
					</ExpandableContainer>
				</div>
			</div>

			<div className="flex flex-col w-full gap-2">
				<div className="flex-none">
					{props.cohortVessels && props.cohortVessels.length > 0 && (
						<CohortDefinition
							key="cohort-co2EmissionsStats"
							title="Cohort (Average By Vessel)"
							minSize={props.minSize}
							maxSize={props.maxSize}
							shipsNumber={props.cohortVessels.length}
							age={cohortVesselInfo.age}
							minAge={props.minAge}
							maxAge={props.maxAge}
							size={cohortVesselInfo.size}
							sizeUnit={cohortVesselInfo.sizeUnit}
							isCohort={true}
						/>
					)}
				</div>
				<div className="flex flex-row h-full w-full gap-4 border border-msi-darkgrey-color-300 rounded">
					<ExpandableContainer title="CO2 Emissions" titleClassName="font-bold pb-2" panelOffsets={panelOffset}>
						<MultiChartDownloadButton
							datasets={cohortChartDatasets}
							filename="co2_emissions_cohort_complete_analysis"
							buttonStyle="primary"
							buttonText="Download All Emissions Data"
						/>
						<div className="flex flex-row h-full w-full gap-2">
							<div className="flex flex-col flex-none gap-1">
								{cohortVesselKeyMetricsDateBased.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
								{!cohortVesselKeyMetricsDateBased.loading &&
									cohortVesselKeyMetricsDateBased &&
									cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData &&
									cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData.length > 0 && (
										<MetricsCard
											className="p-2 space-y-6"
											metrics={[
												{
													label: 'Main Engine (Tank-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].mainEngineEmissions,
												},
												{
													label: 'Pilot (Tank-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].mainEnginePilotEmissions,
												},
												{
													label: 'Auxiliary Engine (Tank-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].auxiliaryEngineEmissions,
												},
												{
													label: 'Boiler (Tank-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].boilerEmissions,
												},
												{
													label: 'Total (Tank-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].co2Emissions,
												},
												{
													label: 'Total (Well-to-Wake)',
													value: cohortVesselKeyMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0].co2EmissionsWtW,
												},
											]}
										/>
									)}
							</div>
							<div className="flex flex-col flex-1 h-full">
								<div className="flex w-full h-full">
									{cohortVesselsMainEngineFuelConsumes.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!cohortVesselsMainEngineFuelConsumes.loading &&
										cohortVesselsMainEngineFuelConsumes &&
										cohortVesselsMainEngineFuelConsumes.cohortVesselsMainEngineFuelConsumesData &&
										cohortVesselsMainEngineFuelConsumes.cohortVesselsMainEngineFuelConsumesData.length > 0 && (
											<MainEngineFuelTypeChart
												key="cohort-mainEngineFuelTypeChart"
												data={cohortVesselsMainEngineFuelConsumes.cohortVesselsMainEngineFuelConsumesData}
											/>
										)}
								</div>
								<div className="flex w-full h-full">
									{cohortVesselsEtsAndFuelEuEmissions.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!cohortVesselsEtsAndFuelEuEmissions.loading &&
										cohortVesselsEtsAndFuelEuEmissions &&
										cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData &&
										cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData.length > 0 && (
											<VesselEtsFuelEuEmissionsChart
												key="cohort-etsEmissionsChart"
												trueSum={cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData[0].etsTrueSum}
												falseSum={cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData[0].etsFalseSum}
												title="CO2 Emission Qualifying for ETS (Tank-to-Wake)"
											/>
										)}
								</div>
								<div className="flex w-full h-full">
									{cohortVesselsEtsAndFuelEuEmissions.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!cohortVesselsEtsAndFuelEuEmissions.loading &&
										cohortVesselsEtsAndFuelEuEmissions &&
										cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData &&
										cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData.length > 0 && (
											<VesselEtsFuelEuEmissionsChart
												key="cohort-fuelEuEmissionsChart"
												trueSum={cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData[0].fuelEuTrueSum}
												falseSum={cohortVesselsEtsAndFuelEuEmissions.cohortVesselsEtsAndFuelEuEmissionsData[0].fuelEuFalseSum}
												title="CO2 Emission Qualifying for FuelEU (Well-to-Wake)"
											/>
										)}
								</div>
								<div className="flex w-full h-full">
									{cohortEmissionsOfEcaSeaAreas.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />}
									{!cohortEmissionsOfEcaSeaAreas.loading &&
										cohortEmissionsOfEcaSeaAreas &&
										cohortEmissionsOfEcaSeaAreas.cohortEmissionsOfEcaSeaAreasData && (
											<EmissionByEcaChart
												key="cohortEmissionsOfEcaSeaAreas"
												data={cohortEmissionsOfEcaSeaAreas.cohortEmissionsOfEcaSeaAreasData}
											/>
										)}
								</div>
							</div>
						</div>
					</ExpandableContainer>
				</div>
			</div>
		</div>
	);
};

export default CohortEmissions;
